import { createAuthClient } from 'better-auth/vue'
import { anonymousClient, emailOTPClient } from 'better-auth/client/plugins'
import {
    appendDesignerCompatibilityRequestHeaders,
    tryApplyClientIncompatibleFromResponse,
} from '~~/layers/base/utils/designerMinAppVersionPolicy'
import { isDeviceOffline } from '~~/layers/base/utils/isDeviceOffline'
import {
    getPreservedBetterAuthGetSessionStubResponse,
    isBetterAuthGetSessionRequest,
} from '~~/layers/base/utils/ecommerceDesignerBetterAuthInjunction'
import { isEcommerceDesignerPath } from '~~/layers/base/utils/isEcommerceDesignerRoute'

type NuxtPublicConfig = {
    betterAuthUrl?: string
}

const CAPACITOR_BEARER_PREF_KEY = 'better_auth_session_bearer'
const CAPACITOR_BEARER_LOCAL_FALLBACK_KEY = 'better_auth_session_bearer_local'

function getCapacitorSafeAuthBaseURL(): string | undefined {
    if (typeof window === 'undefined') {
        return undefined
    }

    const protocol = window.location.protocol
    const isHttpProtocol = protocol === 'http:' || protocol === 'https:'
    if (isHttpProtocol) {
        // Browser/web app can keep same-origin behavior.
        return undefined
    }

    const publicConfig = (
        window as Window & { __NUXT__?: { config?: { public?: NuxtPublicConfig } } }
    ).__NUXT__?.config?.public
    const configuredBase = publicConfig?.betterAuthUrl?.trim()
    if (!configuredBase) {
        return undefined
    }

    // Better Auth expects an absolute http(s) URL and defaults endpoints under /api/auth.
    return `${configuredBase.replace(/\/+$/, '')}/api/auth`
}

/** True when the app runs in Capacitor (or any non-http(s) shell), not in a normal browser tab. */
function isNativeCapacitorShell(): boolean {
    if (typeof window === 'undefined') {
        return false
    }
    const protocol = window.location.protocol
    return protocol !== 'http:' && protocol !== 'https:'
}

async function getCapacitorBearerToken(): Promise<string | null> {
    if (!isNativeCapacitorShell()) {
        return null
    }
    try {
        const { Preferences } = await import('@capacitor/preferences')
        const { value } = await Preferences.get({ key: CAPACITOR_BEARER_PREF_KEY })
        if (value) {
            return value
        }
    } catch {
        // Fall through to localStorage fallback.
    }
    try {
        return window.localStorage.getItem(CAPACITOR_BEARER_LOCAL_FALLBACK_KEY)
    } catch {
        return null
    }
}

/**
 * Session bearer token stored for Capacitor (Preferences / localStorage fallback).
 * Used by Better Auth client fetch hooks and by `useApiFetch` — those must stay in sync.
 */
export async function getCapacitorSessionBearerToken(): Promise<string | null> {
    return getCapacitorBearerToken()
}

/**
 * Explicitly persists a Capacitor session bearer token. Normally this happens automatically via
 * the `onResponse` hook below, which reads the `set-auth-token` response header. But that header
 * can be dropped by a cross-origin hop (Capacitor's `capacitor://localhost` calling a separate
 * HTTPS auth origin) if something in front of the API doesn't preserve
 * `Access-Control-Expose-Headers`, while the JSON response body is never subject to that
 * restriction. Callers that receive a rotated session `token` directly in a response body (e.g.
 * `change-password.vue` after `revokeOtherSessions: true`) should persist it explicitly with this
 * function rather than relying solely on the header round-trip.
 */
export async function setCapacitorSessionBearerToken(token: string | null): Promise<void> {
    return setCapacitorBearerToken(token)
}

function resolveRequestUrl(input: RequestInfo | URL): string {
    if (typeof input === 'string') {
        return input
    }
    if (input instanceof URL) {
        return input.href
    }
    return input.url
}

function isBetterAuthApiRequest(url: string): boolean {
    return url.includes('/api/auth')
}

/** TK-13530 temporary injunction — remove with `isEcommerceDesignerRoute` cutover. */
function shouldStubBetterAuthOnEcommerceDesigner(url: string): boolean {
    if (typeof window === 'undefined') {
        return false
    }
    if (!isEcommerceDesignerPath(window.location.pathname)) {
        return false
    }
    return isBetterAuthApiRequest(url)
}

function stubBetterAuthEmptySessionResponse(): Response {
    return new Response(JSON.stringify({ session: null, user: null }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
    })
}

async function setCapacitorBearerToken(token: string | null): Promise<void> {
    if (!isNativeCapacitorShell()) {
        return
    }
    try {
        const { Preferences } = await import('@capacitor/preferences')
        if (token == null || token === '') {
            await Preferences.remove({ key: CAPACITOR_BEARER_PREF_KEY })
        } else {
            await Preferences.set({ key: CAPACITOR_BEARER_PREF_KEY, value: token })
        }
    } catch {
        // Keep going; localStorage fallback below still allows request auth.
    }
    try {
        if (token == null || token === '') {
            window.localStorage.removeItem(CAPACITOR_BEARER_LOCAL_FALLBACK_KEY)
        } else {
            window.localStorage.setItem(CAPACITOR_BEARER_LOCAL_FALLBACK_KEY, token)
        }
    } catch {
        // Ignore local fallback failures.
    }
}

/**
 * Better Auth client for the frontend. Uses relative URLs so requests go to
 * the same origin (default path /api/auth). For Vue/Nuxt use the composable
 * useBetterAuth() which wraps this client.
 *
 * **Capacitor:** WKWebView often does not persist cross-origin session cookies even with
 * `SameSite=None`. The server `bearer` plugin exposes `set-auth-token` on auth responses; we store
 * it in Capacitor Preferences and send `Authorization: Bearer` on subsequent requests.
 */
export const authClient = createAuthClient({
    baseURL: getCapacitorSafeAuthBaseURL(),
    plugins: [anonymousClient(), emailOTPClient()],
    fetchOptions: {
        /**
         * Skip the real network when the device is already known offline. Throwing here settles
         * Better Auth's `useSession` pending state without a WebKit "Failed to load resource" line
         * for `/get-session` (and other auth calls).
         */
        customFetchImpl: async (input, init) => {
            const url = resolveRequestUrl(input)
            if (shouldStubBetterAuthOnEcommerceDesigner(url)) {
                if (isBetterAuthGetSessionRequest(url)) {
                    const preserved = getPreservedBetterAuthGetSessionStubResponse()
                    if (preserved) {
                        return preserved
                    }
                }
                return stubBetterAuthEmptySessionResponse()
            }
            if (await isDeviceOffline()) {
                throw new TypeError('Failed to fetch')
            }
            return globalThis.fetch(input, init)
        },
        async onRequest(context) {
            try {
                if (!isNativeCapacitorShell()) {
                    return context
                }
                const headers = new Headers(context.headers ?? undefined)
                const authBase = getCapacitorSafeAuthBaseURL()
                await appendDesignerCompatibilityRequestHeaders(headers, authBase)
                const token = await getCapacitorBearerToken()
                if (token) {
                    headers.set('Authorization', `Bearer ${token}`)
                }
                return { ...context, headers }
            } catch {
                // Never block auth requests if token hydration/header mutation fails.
                return context
            }
        },
        async onResponse({ response }) {
            try {
                if (!isNativeCapacitorShell()) {
                    return
                }
                await tryApplyClientIncompatibleFromResponse(response)
                if (!response.ok) {
                    return
                }
                const token = response.headers.get('set-auth-token')
                if (token) {
                    await setCapacitorBearerToken(token)
                }
            } catch {
                // Ignore persistence failures; request already completed.
            }
        },
        async onError(context) {
            try {
                if (!isNativeCapacitorShell()) {
                    return
                }
                const response = (context as { response?: Response }).response
                if (response) {
                    await tryApplyClientIncompatibleFromResponse(response)
                }
            } catch {
                // Ignore; auth error handling proceeds as usual.
            }
        },
        async onSuccess(context) {
            try {
                if (!isNativeCapacitorShell()) {
                    return
                }
                const url = String(context.request?.url ?? '')
                if (url.includes('/sign-out')) {
                    await setCapacitorBearerToken(null)
                }
            } catch {
                // Ignore cleanup failures.
            }
        },
    },
})
